import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, pluck, startWith, switchMap, take } from 'rxjs/operators';

import {
  AdvancedSections,
  DYNAMIC_COMPONENT_DATA_TOKEN,
  EmailBuilderComponent,
  EmailBuilderDataService,
  EmailContentData,
  EmailPreviewHydrationData,
  PLACEHOLDER_LOGO_URL_TOKEN,
  TEMPLATE_HYDRATION_DATA_TOKEN,
  VariableMenuItem,
} from '@galaxy/email-ui/email-builder';
import { TranslateService } from '@ngx-translate/core';
import { SwitchingWorkState, WorkStates } from '@vendasta/rx-utils/work-state';
import { Context } from '../context';

import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { SelectedBusiness } from '../../../shared/preview-business-selector/preview-business-selector.component';
import { TEMPLATE_EXPORTER_KEY, TemplateExporter } from '../dependencies';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MarketingPromptBuilder } from './prompt.service';
import { PROMPT_BUILDER_INJECTION_TOKEN } from '@galaxy/email-ui/email-builder/src/components/suggest-content-dialog/suggest-content-dialog.component';
import { SubscriptionList } from '@vendasta/rx-utils';

const initialContent: EmailContentData = {
  name: '',
  subjectLine: '',
  previewText: '',
  emailSetup: {
    pageSetup: {},
    topLogo: {
      showLogo: 'show',
    },
    colors: {},
    footer: {},
  },
  emailContentBlocks: [],
};

export type TemplateID = string;

interface Fetch {
  templateId: TemplateID;
  partnerId: string;
}

/**
 * TemplateEditorPageComponent is a page that handles loading the content of an
 * email template for editing in memory.
 */
@Component({
  selector: 'app-template-edit-page',
  templateUrl: './template-editor-page.component.html',
  styleUrls: ['./template-editor-page.component.scss'],
  providers: [{ provide: PROMPT_BUILDER_INJECTION_TOKEN, useClass: MarketingPromptBuilder }, MarketingPromptBuilder],
})
export class TemplateEditorPageComponent implements OnDestroy {
  @HostBinding('class') class = 'obs-email-builder-page';

  readonly pageTitle$: Observable<string>;
  private templateId: string;

  readonly previewHydrationData$: Observable<EmailPreviewHydrationData>;
  private readonly selectedBusiness$: Subject<SelectedBusiness> = new Subject<SelectedBusiness>();
  private readonly selectedContact$: Subject<string> = new Subject<string>();

  private readonly fetchState$ = new SwitchingWorkState<Fetch, EmailContentData>((fetch: Fetch) => {
    const emptyContent: EmailContentData = { ...initialContent, emailContentBlocks: [] };
    if (!fetch.templateId) {
      return of(emptyContent);
    }
    return this.dataSvc
      .loadTemplate$(OwnerType.OWNER_TYPE_PARTNER, fetch.partnerId, AppNamespace.CAMPAIGNS, fetch.templateId)
      .pipe(take(1));
  });

  readonly fetchState: WorkStates<EmailContentData> = this.fetchState$;

  @ViewChild('emailBuilder') readonly emailBuilderChild: EmailBuilderComponent;

  @Input() previousPageTitle: string;
  @Input() previousPageURL: string;
  @Input() locale: string = undefined;

  @Input() set context(ctx: Context) {
    let templateId: string;
    if (ctx.isNew()) {
      templateId = ''; // Don't try to load existing content
    } else {
      templateId = ctx.getExistingTemplateId();
    }
    this.fetchState$.startWork({
      partnerId: ctx.getPartnerId(),
      templateId: templateId,
    });
    this.templateId = templateId;
  }

  @Output() legacyTemplateDetected = new EventEmitter<TemplateID>();
  readonly advancedSections: AdvancedSections = {
    topLogo: true,
    pageSetup: true,
    footer: true,
    colors: true,
  };

  private readonly subs = SubscriptionList.new();

  constructor(
    @Inject(TEMPLATE_EXPORTER_KEY) private readonly exporter: TemplateExporter,
    @Inject(TEMPLATE_HYDRATION_DATA_TOKEN)
    @Optional()
    readonly initialPreviewHydrationData$: Observable<EmailPreviewHydrationData>,
    @Inject(PLACEHOLDER_LOGO_URL_TOKEN) readonly placeholderLogo$: Observable<string>,
    @Inject(DYNAMIC_COMPONENT_DATA_TOKEN) readonly dynamicComponents: VariableMenuItem[],
    private readonly alerts: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly dataSvc: EmailBuilderDataService,
    private readonly i18n: TranslateService,
    private readonly router: Router,
  ) {
    this.pageTitle$ = this.fetchState.workResults$.pipe(
      startWith({ name: '' }),
      switchMap((v: EmailContentData) =>
        this.i18n.stream('CAMPAIGN_DETAILS.EDIT_TEMPLATE.TITLE', {
          title: v?.name || '',
        }),
      ),
    );
    const failEvents$ = this.fetchState$.successEvents$.pipe(filter((success) => !success));
    this.subs.add(failEvents$, () => {
      if (this.templateId) {
        this.returnToOld();
      }
    });
    this.subs.add(this.exporter.workState.successEvents$, (success) => {
      if (success) {
        this.emailBuilderChild.saveEmail();
      }
    });
    this.previewHydrationData$ = combineLatest([
      initialPreviewHydrationData$,
      this.selectedBusiness$.pipe(startWith(undefined)),
      this.selectedContact$.pipe(startWith(undefined)),
    ]).pipe(
      map(([initial, selected, contact]: [EmailPreviewHydrationData, SelectedBusiness, string]) => {
        const output = Object.assign({}, initial);
        if (selected) {
          output.business = selected;
        }
        if (contact) {
          output.contactID = contact;
        }
        return output;
      }),
    );
  }

  handleContentChanges(event: EmailContentData): void {
    this.exporter.updateContentData(event);
  }

  handleRenderUpdated(html: string): void {
    this.exporter.updateHTML(html);
  }

  handleRenderFailed(): void {
    this.alerts.errorSnack('There was a problem generating the email preview. Try selecting another business.');
  }

  handleBusinessSelected($event: SelectedBusiness): void {
    this.selectedBusiness$.next($event);
  }

  handleContactSelected($event: string): void {
    this.selectedContact$.next($event);
  }

  ngOnDestroy(): void {
    this.subs.destroy();
  }

  returnToOld(): void {
    const campaignId$ = this.route.queryParams.pipe(pluck('campaignId'));
    const campaignStepId$ = this.route.queryParams.pipe(pluck('campaignStepId'));
    combineLatest([campaignId$, campaignStepId$]).subscribe(([campaignId, campaignStepId]) =>
      this.router.navigate([`/marketing/campaign/${campaignId}/${campaignStepId}/template/${this.templateId}`], {
        queryParams: { angular: 'true' },
      }),
    );
  }

  protected readonly undefined = undefined;
}
